/* product-finder.js — "Find my setup" quiz, ported from the verified mockup
 * (PRODUCT-FINDER-MOCKUP-2026-08-21.html). The match engine below
 * (TIER_META / pickTier / computeMatch / boxFor) is that same tested logic,
 * unchanged — only the DOM building and the real fetch() submit are new.
 *
 * SCOPE NOTE: English only. The mockup's copy was never translated, and
 * porting DE/FR/NL is real work not done here — see the build plan.
 * "In the box" content, however, DOES come translated: it's read from
 * each variant's own metafield in the Liquid bootstrap (boxHtml), not
 * generated client-side, so that part follows the page's locale already.
 */
(function () {
  "use strict";

  var root = document.currentScript.closest(".pf-root") ||
    document.querySelector('[data-pf]');
  if (!root) return;
  var dataEl = document.getElementById(root.id.replace("pf-root-", "pf-data-"));
  if (!dataEl) return;
  var PF = JSON.parse(dataEl.textContent);
  PF.domain = root.dataset.domain;
  PF.store = root.dataset.store;
  PF.dial = root.dataset.dial;
  PF.endpoint = root.dataset.endpoint;

  // Copy lookup. Falls back to the key itself so a missing translation shows
  // something diagnosable rather than an empty element.
  var I18N = PF.i18n || {};
  function t(key, vars) {
    var s = I18N[key];
    if (s == null) s = key;
    if (vars) Object.keys(vars).forEach(function (k) { s = s.split("{" + k + "}").join(vars[k]); });
    return s;
  }
  // "Incl. VAT" / "Excl. TVA" / "Incl. GST" — the term is resolved in Liquid
  // from the buyer's country, the basis from shop.taxes_included. EU is
  // tax-EXCLUSIVE, so this label is load-bearing, not decoration.
  function taxLabel() {
    return t(PF.taxIncluded ? "incl_tax" : "excl_tax", { term: PF.taxTerm || "VAT" });
  }

  // PF.addons comes from Liquid keyed by the real Shopify product HANDLE
  // (e.g. "speediance-pilates-kit", "body-fat-scale" on AU/NZ vs
  // "smart-body-composition-scale" elsewhere). The match engine below is
  // written against short, store-independent keys, same as the mockup this
  // was ported from. Normalize once here so every downstream PF.addons.X
  // check works the same way regardless of which handle a given store
  // actually uses for that accessory.
  var HANDLE_TO_KEY = {
    "speediance-pilates-kit": "pilates",
    "storage-rack": "rack",
    "weight-lifting-belt": "belt",
    "speediance-powergrip": "grips",
    "speediance-powergrips": "grips",
    "speediance-ski-erg-handles": "ski",
    "ski-handles": "ski",
    "smart-body-composition-scale": "scale",
    "body-fat-scale": "scale",
    "speediance-velonix": "velonix",
    "rowing-bench-2-0": "rowing",
    "rowing-bench-copy": "rowing"
  };
  (function normalizeAddons() {
    var normalized = {};
    Object.keys(PF.addons || {}).forEach(function (handle) {
      var key = HANDLE_TO_KEY[handle];
      if (key && PF.addons[handle] && typeof PF.addons[handle] === "object") normalized[key] = PF.addons[handle];
    });
    PF.addons = normalized;
  })();

  var TIER_META = {
    // Gym Monster 2 packages
    "Basic 2.0": { bench: "none", rowing: false },
    "Core 2.0": { bench: "none", rowing: false },
    "Works 2.0": { bench: "flat", rowing: false },
    "Works Plus 2.0": { bench: "adjustable", rowing: false },
    "Family 2.0": { bench: "flat", rowing: true },
    "Family Plus 2.0": { bench: "adjustable", rowing: true },
    // Gym Monster 3 packages. Studio adds the Incline Bench, Station adds the
    // AeroRow Bench and Rowing Bar on top of it, so they map onto the same two
    // axes the matcher already reasons about.
    "Core": { bench: "none", rowing: false },
    "Studio": { bench: "adjustable", rowing: false },
    "Station": { bench: "adjustable", rowing: true }
  };
  var ladder = [];
  PF.variants.forEach(function (v) { if (ladder.indexOf(v.pkg) === -1 && TIER_META[v.pkg]) ladder.push(v.pkg); });

  // The two machine names, read off the data rather than hardcoded, so the
  // same engine runs on the Gym Monster 2 and the Gym Monster 3 page. The
  // uprated model is the one whose name ends in S; falling back to the
  // section-supplied names keeps a single-version product from crashing here.
  var VERS = [];
  PF.variants.forEach(function (v) { if (v.ver && VERS.indexOf(v.ver) === -1) VERS.push(v.ver); });
  var S_VER = PF.sVer || VERS.filter(function (x) { return /s$/i.test(x); })[0] || VERS[VERS.length - 1] || "";
  var BASE_VER = PF.baseVer || VERS.filter(function (x) { return x !== S_VER; })[0] || VERS[0] || "";
  if (S_VER === BASE_VER) S_VER = "";

  var A = { goal: null, who: null, load: null, styles: [], space: null, when: null };
  var MATCH = null;

  function money(n) {
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: PF.currency, maximumFractionDigits: 0 }).format(n);
    } catch (e) {
      return PF.currency + " " + Math.round(n);
    }
  }
  function avail(v) { return v.available || v.inventoryPolicy === "continue"; }
  function findVariant(ver, pkg) {
    for (var i = 0; i < PF.variants.length; i++) if (PF.variants[i].ver === ver && PF.variants[i].pkg === pkg) return PF.variants[i];
    return null;
  }
  function has(s) { return A.styles.indexOf(s) !== -1; }
  function boxFromHtml(html) {
    var tmp = document.createElement("div");
    tmp.innerHTML = html || "";
    return Array.prototype.map.call(tmp.querySelectorAll("li"), function (li) { return li.textContent.trim(); }).filter(Boolean);
  }
  // Fallback box contents when a variant has no description metafield (the 2S
  // variants have none), derived from TIER_META. Translated, because the
  // hardcoded English version rendered English chips under a French heading.
  function genericBox(tier) {
    var m = TIER_META[tier], items = [];
    if (m.bench === "flat") items.push(t("box_flat_bench"));
    if (m.bench === "adjustable") items.push(t("box_adj_bench"));
    if (m.rowing) { items.push(t("box_rowing_bench")); items.push(t("box_rowing_bar")); }
    return items.concat([t("box_screen"), t("box_handles"), t("box_barbell"), t("box_tricep"),
      t("box_ankle"), t("box_ring"), t("box_ring_clip"), t("box_mat"), t("box_belt_ext"), t("box_hooks")]);
  }
  // Some variants carry no image of their own and Shopify falls back to the
  // product featured image, which is the bench-less Basic photo. Substitute
  // the closest package that DOES have a real photo, matched on bench/rowing,
  // so a Family package never renders as a machine with no bench.
  function imageFor(v) {
    if (v.ownImg) return v.ownImg;
    var want = TIER_META[v.pkg];
    if (!want) return v.img;
    var score = function (x) { return x === "adjustable" ? 2 : x === "flat" ? 1 : 0; };
    // Penalise a bench-less photo when the target tier HAS a bench: without
    // it, "Works 2.0" (flat bench) tied between the Basic photo (no bench)
    // and Works Plus, and picked the bench-less one, showing a customer a
    // machine missing the bench they are paying for. Works 2.0 is sold out
    // on EU/UK but in stock on NZ, so this is a real customer-facing path.
    var cost = function (c) {
      var m = TIER_META[c.pkg];
      return Math.abs(score(m.bench) - score(want.bench))
        + (m.rowing !== want.rowing ? 2 : 0)
        + (c.ver !== v.ver ? 1 : 0)
        + (want.bench !== "none" && m.bench === "none" ? 1 : 0);
    };
    var best = PF.variants
      .filter(function (c) { return c.ownImg && TIER_META[c.pkg]; })
      .sort(function (a, b) { return cost(a) - cost(b); })[0];
    return best ? best.ownImg : v.img;
  }
  function priceAddon(key, meta) {
    if (key === "velonix" && PF.velonixEnabled) return { was: meta.price, now: meta.price - PF.velonixFixedAmt, tag: t("velonix_tag", { amount: money(PF.velonixFixedAmt) }) };
    if (key === "velonix" || key.indexOf("rowing") === 0) return { was: null, now: meta.price, tag: null };
    if (PF.pct20 > 0) return { was: meta.price, now: Math.round(meta.price * (1 - PF.pct20 / 100)), tag: t("discount_tag", { pct: PF.pct20 }) };
    return { was: null, now: meta.price, tag: null };
  }

  function pickTier(wantsRow, wantsAdjustable, minimal) {
    var benchPref = minimal ? ["none", "flat", "adjustable"] : wantsAdjustable ? ["adjustable", "flat", "none"] : ["flat", "adjustable", "none"];
    var rowOrder = [wantsRow, !wantsRow];
    for (var r = 0; r < rowOrder.length; r++) {
      for (var b = 0; b < benchPref.length; b++) {
        var hit = ladder.filter(function (cand) { return TIER_META[cand].bench === benchPref[b] && TIER_META[cand].rowing === rowOrder[r]; })[0];
        if (hit) return hit;
      }
    }
    return ladder[0];
  }

  function computeMatch() {
    var wantsRow = has("rowing") || (A.goal === "lean" && A.space === "gym");
    var wantsIncl = has("incline") || has("strength") || A.goal === "strength" || A.goal === "allround";
    var minimal = !wantsIncl && !wantsRow && (A.goal === "mobility" || has("rehab") || has("yoga") || has("pilates")) && A.space === "tight";
    var tier = pickTier(wantsRow, wantsIncl, minimal);
    var heavy = A.load === "heavy";
    var growth = A.who === "family" && A.load === "mid";
    var ver = (heavy || growth) && S_VER ? S_VER : BASE_VER;
    var notes = [];
    var hasS = !!S_VER && PF.variants.some(function (v) { return v.ver === S_VER; });
    // The note was previously a hardcoded English sentence, so a French or
    // German shopper on a store without the uprated model got one English
    // line in the middle of translated copy. note_no_2s already exists in
    // every locale block for exactly this case.
    if (S_VER && ver === S_VER && !hasS) { ver = BASE_VER; notes.push(t("note_no_2s")); }

    function dist(cand) {
      var score = function (x) { return x === "adjustable" ? 2 : x === "flat" ? 1 : 0; };
      return Math.abs(score(TIER_META[cand].bench) - score(TIER_META[tier].bench)) + (TIER_META[cand].rowing !== TIER_META[tier].rowing ? 2 : 0);
    }
    var v = findVariant(ver, tier);
    if (!v || !avail(v)) {
      var sameVer = PF.variants.filter(function (x) { return x.ver === ver && avail(x); }).sort(function (a, b) { return dist(a.pkg) - dist(b.pkg); });
      if (sameVer[0]) {
        if (sameVer[0].pkg !== tier) {
          notes.push(ladder.indexOf(tier) !== -1
            ? t("note_sold_out", { tier: tier })
            : t("note_not_sold", { tier: tier }));
        }
        v = sameVer[0]; tier = v.pkg;
      } else {
        var other = PF.variants.filter(avail).sort(function (a, b) { return b.price - a.price; })[0];
        if (other) { notes.push(t("note_ver_sold_out", { ver: ver })); v = other; ver = v.ver; tier = v.pkg; }
        else { v = PF.variants.filter(function (x) { return x.ver === ver; }).sort(function (a, b) { return dist(a.pkg) - dist(b.pkg); })[0] || PF.variants[0]; tier = v.pkg; }
      }
    }

    var add = [];
    if (has("pilates") && PF.addons.pilates) add.push({ k: "pilates", why: t("addon_pilates_why"), on: true });
    if (has("ski") && PF.addons.ski) add.push({ k: "ski", why: t("addon_ski_why"), on: true });
    if (has("cycling") && PF.addons.velonix) add.push({ k: "velonix", why: t("addon_velonix_why"), on: false });
    if (has("rowing") && !TIER_META[tier].rowing && PF.addons.rowing) add.push({ k: "rowing", why: t("addon_rowing_why"), on: true });
    if (A.space === "tight" && PF.addons.rack) add.push({ k: "rack", why: t("addon_rack_why"), on: true });
    if ((A.goal === "strength" || A.load === "heavy") && PF.addons.grips) add.push({ k: "grips", why: t("addon_grips_why"), on: false });
    if (A.goal === "strength" && PF.addons.belt) add.push({ k: "belt", why: t("addon_belt_why"), on: false });
    if ((A.goal === "lean" || A.who !== "solo") && PF.addons.scale) add.push({ k: "scale", why: A.goal === "lean" ? t("addon_scale_lean_why") : t("addon_scale_multi_why"), on: false });

    var why = [];
    var isUprated = !!S_VER && ver === S_VER;
    why.push({ t: isUprated ? t("why_120_t") : t("why_100_t"),
      b: isUprated ? (heavy ? t("why_120_heavy_b") : t("why_120_growth_b"))
         : (A.load === "light" || A.load === "unsure" ? t("why_100_light_b") : t("why_100_mid_b")) });
    var benchNow = TIER_META[tier].bench;
    why.push({ t: benchNow === "adjustable" ? t("why_bench_adj_t") : benchNow === "none" ? t("why_bench_none_t") : t("why_bench_flat_t"),
      b: benchNow === "adjustable" ? t("why_bench_adj_b") : benchNow === "none" ? t("why_bench_none_b") : t("why_bench_flat_b") });
    if (TIER_META[tier].rowing) why.push({ t: t("why_row_yes_t"), b: t("why_row_yes_b") });
    else why.push({ t: t("why_row_no_t"), b: t("why_row_no_b") });
    why.push({ t: A.who === "solo" ? t("why_solo_t") : A.who === "two" ? t("why_two_t") : t("why_family_t"),
      b: A.who === "solo" ? t("why_solo_b") : t("why_multi_b") });

    var ruled = "";
    var rowTier = ladder.filter(function (cand) { return TIER_META[cand].rowing && TIER_META[cand].bench === TIER_META[tier].bench; })[0] || ladder.filter(function (cand) { return TIER_META[cand].rowing; })[0];
    var fp = rowTier ? findVariant(ver, rowTier) : null;
    var s2 = S_VER ? findVariant(S_VER, tier) : null;
    if (!TIER_META[tier].rowing && fp && avail(fp)) ruled = t("ruled_row", { tier: "<b>" + rowTier + "</b>", amount: money(fp.price - v.price) });
    else if (ver === BASE_VER && s2 && avail(s2)) ruled = t("ruled_2s", { amount: money(s2.price - v.price) });
    else if (S_VER && ver === S_VER) ruled = t("ruled_kept_2s");
    else ruled = t("ruled_minimal");

    var box = boxFromHtml(v.boxHtml);
    if (!box.length) box = genericBox(tier);
    // What the uprated model adds over the base one. The two machines differ
    // in different ways, so the list is per family: the 2S adds the
    // self-lowering platform, which the Gym Monster 3 already has on both
    // versions, and the 3S adds the bigger motors instead.
    var extra = [];
    if (S_VER && ver === S_VER) {
      extra = PF.family === "gm3"
        ? [t("box_3s_resistance"), t("box_3s_motors"), t("box_3s_handles")]
        : [t("box_2s_resistance"), t("box_2s_platform"), t("box_2s_handles")];
    }

    return { v: v, ver: ver, tier: tier, add: add, why: why, ruled: ruled, notes: notes, box: extra.concat(box) };
  }

  /* ---------- DOM ---------- */
  // Every user-facing string resolves through t() so the page's own locale
  // drives the copy. Previously these were hardcoded English literals, which
  // is why a French storefront rendered an all-English quiz.
  var QUESTIONS = [
    { key: "goal", multi: false, num: t("q_goal_num"), h: t("q_goal_h"), sub: t("q_goal_sub"),
      opts: [["strength", t("goal_strength"), t("goal_strength_d")], ["lean", t("goal_lean"), t("goal_lean_d")], ["mobility", t("goal_mobility"), t("goal_mobility_d")], ["allround", t("goal_allround"), t("goal_allround_d")]] },
    { key: "who", multi: false, num: t("q_who_num"), h: t("q_who_h"), sub: t("q_who_sub"),
      opts: [["solo", t("who_solo"), t("who_solo_d")], ["two", t("who_two"), t("who_two_d")], ["family", t("who_family"), t("who_family_d")]] },
    { key: "load", multi: false, num: t("q_load_num"), h: t("q_load_h"), sub: t("q_load_sub"),
      opts: [["light", t("load_light"), t("load_light_d")], ["mid", t("load_mid"), t("load_mid_d")], ["heavy", t("load_heavy"), t("load_heavy_d")], ["unsure", t("load_unsure"), t("load_unsure_d")]] },
    { key: "styles", multi: true, num: t("q_styles_num"), h: t("q_styles_h"), sub: t("q_styles_sub"),
      opts: [["strength", t("st_strength"), t("st_strength_d")], ["rowing", t("st_rowing"), t("st_rowing_d")], ["ski", t("st_ski"), t("st_ski_d")], ["pilates", t("st_pilates"), t("st_pilates_d")], ["cycling", t("st_cycling"), t("st_cycling_d")], ["yoga", t("st_yoga"), t("st_yoga_d")], ["incline", t("st_incline"), t("st_incline_d")], ["rehab", t("st_rehab"), t("st_rehab_d")]] },
    { key: "space", multi: false, num: t("q_space_num"), h: t("q_space_h"), sub: t("q_space_sub"),
      opts: [["tight", t("sp_tight"), t("sp_tight_d")], ["room", t("sp_room"), t("sp_room_d")], ["gym", t("sp_gym"), t("sp_gym_d")]] },
    { key: "when", multi: false, num: t("q_when_num"), h: t("q_when_h"), sub: "",
      opts: [["now", t("wh_now"), ""], ["soon", t("wh_soon"), ""], ["research", t("wh_research"), ""], ["demo", t("wh_demo"), ""]] }
  ];

  var overlay = root.querySelector("[data-pf-overlay]");
  var body = root.querySelector("[data-pf-body]");
  var bar = root.querySelector("[data-pf-bar]");
  var stepLabel = root.querySelector("[data-pf-steplabel]");
  var TOTAL_STEPS = QUESTIONS.length + 1; // Questions then an ungated recommendation

  function el(tag, cls, html) { var e = document.createElement(tag); if (cls) e.className = cls; if (html != null) e.innerHTML = html; return e; }

  function renderQuestion(qi) {
    var q = QUESTIONS[qi];
    var wrap = el("div", "pf-q");
    wrap.appendChild(el("div", "pf-qnum", q.num));
    wrap.appendChild(el("h2", null, q.h));
    if (q.sub) wrap.appendChild(el("p", "pf-sub", q.sub));
    var opts = el("div", "pf-opts" + (q.key === "styles" || q.key === "when" ? " pf-opts--two" : ""));
    q.opts.forEach(function (o) {
      var btn = el("button", "pf-choice");
      btn.type = "button"; btn.dataset.v = o[0];
      btn.innerHTML = '<span class="pf-ci"></span><span><b>' + o[1] + "</b>" + (o[2] ? "<small>" + o[2] + "</small>" : "") + "</span>";
      var selected = q.multi ? A[q.key].indexOf(o[0]) !== -1 : A[q.key] === o[0];
      if (selected) btn.classList.add("is-sel");
      btn.addEventListener("click", function () {
        if (q.multi) {
          btn.classList.toggle("is-sel");
          var idx = A[q.key].indexOf(o[0]);
          if (btn.classList.contains("is-sel")) { if (idx === -1) A[q.key].push(o[0]); } else if (idx !== -1) A[q.key].splice(idx, 1);
        } else {
          Array.prototype.forEach.call(opts.querySelectorAll(".pf-choice"), function (b) { b.classList.remove("is-sel"); });
          btn.classList.add("is-sel"); A[q.key] = o[0];
        }
        nextBtn.disabled = q.multi ? A[q.key].length === 0 : !A[q.key];
      });
      opts.appendChild(btn);
    });
    wrap.appendChild(opts);
    var acts = el("div", "pf-acts");
    if (qi > 0) { var back = el("button", "pf-back", t("back")); back.type = "button"; back.addEventListener("click", function () { goTo(qi - 1); }); acts.appendChild(back); }
    else acts.appendChild(el("span"));
    var nextBtn = el("button", "pf-btn", qi === QUESTIONS.length - 1 ? t("see_match") : t("next"));
    nextBtn.type = "button";
    nextBtn.disabled = q.multi ? A[q.key].length === 0 : !A[q.key];
    nextBtn.addEventListener("click", function () { goTo(qi + 1); });
    acts.appendChild(nextBtn);
    wrap.appendChild(acts);
    return wrap;
  }

  function renderResult() {
    var m = MATCH = MATCH || computeMatch();
    var wrap = el("div", "pf-result");
    var pill = el("span", "pf-pill", "\u2713 " + t("your_match"));
    wrap.appendChild(pill);
    wrap.appendChild(el("h2", null, m.ver + " · " + m.tier));
    var hero = el("div", "pf-hero");
    var heroImg = new Image();
    heroImg.alt = m.ver + " " + m.tier;
    // Eager, not lazy: this is the single hero image of the result the
    // customer just asked for, and lazy-loading inside a scrolling modal
    // container is unreliable (the intersection root is the modal, not the
    // viewport, so it can sit unloaded at 0x0). Nothing to gain from
    // deferring one image that is the point of the screen.
    heroImg.loading = "eager";
    heroImg.decoding = "async";
    // If the chosen image 404s, fall back to the product image, then hide the
    // frame entirely rather than leaving an empty grey box.
    heroImg.onerror = function () {
      if (heroImg.src !== m.v.img && m.v.img) { heroImg.src = m.v.img; return; }
      hero.style.display = "none";
    };
    heroImg.src = imageFor(m.v);
    hero.appendChild(heroImg);
    wrap.appendChild(hero);
    // Fragments are translated too, and the {bits}/{who} placeholders are
    // substituted into a translated sentence, so each language controls its
    // own word order and prepositions rather than getting English grammar
    // with translated nouns dropped in.
    var bits = [];
    if (has("strength") || A.goal === "strength") bits.push(t("bit_strength"));
    if (TIER_META[m.tier].rowing || has("rowing")) bits.push(t("bit_rowing"));
    if (has("ski")) bits.push(t("bit_ski"));
    if (has("pilates")) bits.push(t("bit_pilates"));
    if (has("cycling")) bits.push(t("bit_cycling"));
    if (has("yoga") || A.goal === "mobility") bits.push(t("bit_mobility"));
    var whoTxt = A.who === "solo" ? t("who_you") : A.who === "two" ? t("who_you_partner") : t("who_household");
    wrap.appendChild(el("p", "pf-lede", t("lede", { bits: (bits.slice(0, 3).join(", ") || t("bits_fallback")), who: whoTxt })));
    wrap.appendChild(el("div", "pf-price", '<span class="now">' + money(m.v.price) + '</span> <span class="was">' + money(m.v.compareAt) + '</span> <span class="pf-tax">' + taxLabel() + "</span>"));

    // Show the recommendation before asking for contact information. Fulfillment
    // confirmation belongs to the guide form and follows a successful response.
    if (window.BMSalesExperience) {
      var saveRow = el("div", "bm-sales-actions");
      var save = el("button", null, window.BMSalesExperience.guideLabel);
      save.type = "button";
      save.addEventListener("click", function () {
        closeFinderModal();
        window.BMSalesExperience.openGuide({variant_id: m.v.id, finder_answers: A});
      });
      saveRow.appendChild(save); wrap.appendChild(saveRow);
    }

    wrap.appendChild(el("h3", "pf-h", t("why_this")));
    var why = el("div", "pf-why");
    m.why.forEach(function (w) { why.appendChild(el("div", "pf-whyc", "<b>" + w.t + "</b><span>" + w.b + "</span>")); });
    wrap.appendChild(why);
    wrap.appendChild(el("div", "pf-ruled", m.ruled));

    wrap.appendChild(el("h3", "pf-h", t("in_the_box")));
    var chips = el("div", "pf-chips");
    m.box.forEach(function (c) { chips.appendChild(el("span", "pf-chip", c)); });
    wrap.appendChild(chips);

    if (PF.careAvailable) wrap.appendChild(el("div", "pf-care", "<b>" + t("care_title") + "</b>" + t("care_body", { price: money(PF.carePrice), domain: PF.domain })));

    wrap.appendChild(el("h3", "pf-h", t("worth_adding")));
    var addonsWrap = el("div", "pf-addons");
    var rows = []; // {addon, checkbox} pairs, only for addons that actually rendered
    m.add.forEach(function (a) {
      var meta = PF.addons[a.k];
      if (!meta) return;
      var priced = priceAddon(a.k, meta);
      a._now = priced.now;
      var row = el("label", "pf-addon");
      row.innerHTML = '<input type="checkbox" ' + (a.on ? "checked" : "") + '><img src="' + meta.img + '"><span class="an"><b>' + meta.name + "</b><small>" + a.why + "</small>" + (priced.tag ? '<small class="disc">' + priced.tag + "</small>" : "") + '</span><span class="ap">' + (priced.was ? "<s>" + money(priced.was) + "</s>" : "") + money(priced.now) + "</span>";
      var cb = row.querySelector("input");
      cb.addEventListener("change", updateTotal);
      rows.push({ addon: a, cb: cb });
      addonsWrap.appendChild(row);
    });
    if (!rows.length) addonsWrap.appendChild(el("div", "pf-empty", t("nothing_to_add")));
    wrap.appendChild(addonsWrap);
    var totalEl = el("div", "pf-total",
      "<span>" + t("setup_total") + '</span><span class="pf-total-r"><span data-pf-total>' + money(m.v.price) + '</span><small class="pf-tax">' + taxLabel() + "</small></span>");
    wrap.appendChild(totalEl);
    function updateTotal() {
      var sum = m.v.price;
      rows.forEach(function (r) { if (r.cb.checked) sum += r.addon._now; });
      totalEl.querySelector("[data-pf-total]").textContent = money(sum);
    }
    updateTotal();
    // reveal() builds the submit payload from this, not from DOM order.
    MATCH._selected = function () {
      return rows.filter(function (r) { return r.cb.checked; })
        .map(function (r) { return { key: r.addon.k, name: (PF.addons[r.addon.k] || {}).name, price: r.addon._now }; });
    };

    var ctaWrap = el("div", "pf-cta-col");
    var addBtn = el("button", "pf-btn pf-block", t("add_to_cart"));
    addBtn.type = "button";
    addBtn.addEventListener("click", function () {
      var items = [{ id: m.v.id, quantity: 1 }];
      rows.forEach(function (r) { var meta = PF.addons[r.addon.k]; if (r.cb.checked && meta && meta.id) items.push({ id: meta.id, quantity: 1 }); });
      fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items: items }) })
        .then(function (r) { if (r.ok) window.location.href = "/checkout"; else alert("Could not add to cart — please try again."); })
        .catch(function () { alert("Could not add to cart — please try again."); });
    });
    ctaWrap.appendChild(addBtn);
    wrap.appendChild(ctaWrap);

    var restart = el("button", "pf-restart", t("change_answers")); restart.type = "button";
    restart.addEventListener("click", function () { A = { goal: null, who: null, load: null, styles: [], space: null, when: null }; goTo(0); });
    wrap.appendChild(restart);
    return wrap;
  }

  function goTo(i) {
    body.innerHTML = "";
    if (i < QUESTIONS.length) {
      body.appendChild(renderQuestion(i));
      stepLabel.textContent = t("step_of", { n: i + 1, total: QUESTIONS.length });
      bar.style.transform = "scaleX(" + ((i + 1) / TOTAL_STEPS) + ")";
    } else if (i === QUESTIONS.length) {
      MATCH = computeMatch();
      body.appendChild(renderResult());
      stepLabel.textContent = t("your_recommendation");
      bar.style.transform = "scaleX(1)";
    }
  }

  // Overlay open/close is opacity+transform driven (see product-finder.css),
  // not the `hidden` attribute — that let it snap instantly with no way to
  // transition. A discrete click-triggered open/close isn't a drag gesture,
  // so a CSS transition is the right tool here (not a JS spring — those
  // earn their cost on continuously-interruptible pointer-tracked motion,
  // which nothing in this widget does).
  function openFinderModal() {
    document.body.style.overflow = "hidden";
    A = { goal: null, who: null, load: null, styles: [], space: null, when: null };
    // Populate BEFORE opening, then force a style flush, then add the class.
    // The transition still gets a clean "before" frame (that was the original
    // reason for deferring), but without depending on requestAnimationFrame:
    // rAF does not fire in a hidden/backgrounded tab, so the previous
    // deferred version could open a permanently EMPTY modal.
    goTo(0);
    void overlay.offsetHeight;
    overlay.classList.add("is-open");
  }
  function closeFinderModal() {
    overlay.classList.remove("is-open");
    document.body.style.overflow = "";
  }
  root.querySelectorAll("[data-pf-open]").forEach(function (b) { b.addEventListener("click", openFinderModal); });
  root.querySelectorAll("[data-pf-close]").forEach(function (b) { b.addEventListener("click", closeFinderModal); });
  document.addEventListener("keydown", function (e) { if (e.key === "Escape" && overlay.classList.contains("is-open")) closeFinderModal(); });

  /* The theme's own "Complete your setup" accessories widget is not where
   * the template puts it — a script inside gm2-addons.liquid physically
   * relocates it into the sticky variant-picker column after page load, so
   * it visually sits right under the package tiles. A plain new section
   * (this one, before this fix) has no such relocation and just renders
   * wherever the template's section order puts it — which, because the
   * left-column image gallery is much taller than the right-column info
   * box, is a long way below the whole gallery. Fix: move the CTA card
   * (not the modal — that is position:fixed and does not care where its
   * wrapper sits) into that same sticky column, right after that widget.
   *
   * Tried "right before" first (so this would read as the first thing
   * offered, ahead of the accessories upsell) but lost the ordering in
   * production every time, including against a brute-force 200ms poll —
   * gm2-addons' own script appears to keep re-claiming a fixed slot
   * immediately after the variant picker, not a one-time move. Rather than
   * fight an unknown recurring script, this targets "right after" instead,
   * which the DOM settles into naturally and which reads fine anyway: see
   * the package tiles, see the accessory upsell, then "still not sure? get
   * help" right before Add to Cart. Never touches gm2-addons' own element,
   * only reads where it currently is. */
  (function relocateCta() {
    var wrapper = document.querySelector(".variant-selects__wrapper");
    var cta = root.querySelector(".pf-cta");
    if (!wrapper || !cta) return;

    // Re-asserted on a timer rather than done once: idempotent, so this
    // costs nothing once settled, and it self-heals if the other script
    // reclaims its slot again after this one has already placed the CTA.
    function place() {
      var anchor = wrapper.querySelector("#gm2-addons-root, [data-gm2ax]");
      if (anchor && anchor.parentElement === wrapper) {
        if (anchor.nextElementSibling !== cta) anchor.insertAdjacentElement("afterend", cta);
      } else if (wrapper.lastElementChild !== cta) {
        wrapper.appendChild(cta);
      }
    }

    place();
    var ticks = 0;
    var interval = setInterval(function () {
      place();
      ticks++;
      if (ticks > 40) clearInterval(interval); // ~8s at 200ms
    }, 200);
  })();
})();

